/**
 * Theme utility functions for dynamic color theming.
 * Used by PublicBookingPage and SettingsPage.
 */

export const DEFAULT_PRIMARY = '#10B981';
export const DEFAULT_ACCENT = '#06B6D4';

/**
 * Validate hex color string (#RGB or #RRGGBB)
 */
export const isValidHex = (hex) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);

/**
 * Parse hex to { r, g, b }
 */
export const hexToRgb = (hex) => {
  // Expand shorthand (#RGB → #RRGGBB)
  const full = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : null;
};

/**
 * Get rgba() string from hex + alpha
 */
export const hexToRgba = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
};

/**
 * Theme presets: layout colors (bg, cards, text, borders).
 * Primary/accent colors are user-chosen and applied on top.
 */
export const THEME_PRESETS = {
  light: {
    id: 'light',
    name: 'Clean Light',
    nameGr: 'Φωτεινό',
    bg: '#F9FAFB',
    cardBg: '#FFFFFF',
    cardBgStyle: '#FFFFFF',
    cardBorder: '#E5E7EB',
    cardShadow: '0 10px 25px rgba(0,0,0,0.06)',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    inputBg: '#FFFFFF',
    inputBorder: '#D1D5DB',
    inputText: '#111827',
    errorColor: '#EF4444',
    overlayBg: 'rgba(249,250,251,0.98)',
    unselectedBg: 'rgba(243,244,246,0.7)',
    inactiveCircle: 'rgba(209,213,219,0.6)',
  },
  medical: {
    id: 'medical',
    name: 'Soft Medical',
    nameGr: 'Ιατρικό',
    bg: '#EFF6FF',
    cardBg: '#F0F9FF',
    cardBgStyle: '#F0F9FF',
    cardBorder: '#BFDBFE',
    cardShadow: '0 10px 25px rgba(0,0,0,0.06)',
    textPrimary: '#1E293B',
    textSecondary: '#64748B',
    inputBg: '#FFFFFF',
    inputBorder: '#CBD5E1',
    inputText: '#1E293B',
    errorColor: '#EF4444',
    overlayBg: 'rgba(239,246,255,0.98)',
    unselectedBg: 'rgba(224,242,254,0.5)',
    inactiveCircle: 'rgba(148,163,184,0.5)',
  },
  dark: {
    id: 'dark',
    name: 'Dark Professional',
    nameGr: 'Σκούρο',
    bg: '#1A202C',
    cardBg: '#111827',
    cardBgStyle: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
    cardBorder: '#374151',
    cardShadow: '0 10px 25px rgba(0,0,0,0.3)',
    textPrimary: '#FFFFFF',
    textSecondary: '#9CA3AF',
    inputBg: '#1A202C',
    inputBorder: '#4A5568',
    inputText: '#FFFFFF',
    errorColor: '#EF4444',
    overlayBg: 'rgba(26,32,44,0.98)',
    unselectedBg: 'rgba(17,24,39,0.5)',
    inactiveCircle: 'rgba(55,65,81,0.6)',
  },
};

/**
 * Build a complete theme object from primary + accent hex colors and a layout preset.
 * When colors are null/invalid, defaults replicate the current hardcoded appearance.
 */
export const buildTheme = (primary, accent, themeId = 'dark') => {
  const p = isValidHex(primary) ? primary : DEFAULT_PRIMARY;
  const a = isValidHex(accent) ? accent : DEFAULT_ACCENT;
  const preset = THEME_PRESETS[themeId] || THEME_PRESETS.dark;

  return {
    primary: p,
    accent: a,
    // Gradients
    gradient: `linear-gradient(135deg, ${p} 0%, ${a} 100%)`,
    gradientHorizontal: `linear-gradient(90deg, ${p}, ${a})`,
    // Selection highlight gradients (low opacity)
    gradientSelection: `linear-gradient(135deg, ${hexToRgba(p, 0.1)} 0%, ${hexToRgba(a, 0.1)} 100%)`,
    // Opacity variants for backgrounds
    primaryUltraLight: hexToRgba(p, 0.04),
    primaryMedium: hexToRgba(p, 0.12),
    primaryLight: hexToRgba(p, 0.15),
    // Glow/shadow effects
    primaryShadowLight: hexToRgba(p, 0.2),
    primaryShadow: hexToRgba(p, 0.3),
    primaryGlow: hexToRgba(p, 0.4),
    // Text / border (primary color used directly)
    primaryText: p,
    primaryBorder: p,
    // Layout preset colors
    bg: preset.bg,
    cardBg: preset.cardBg,
    cardBgStyle: preset.cardBgStyle,
    cardBorder: preset.cardBorder,
    cardShadow: preset.cardShadow,
    textPrimary: preset.textPrimary,
    textSecondary: preset.textSecondary,
    inputBg: preset.inputBg,
    inputBorder: preset.inputBorder,
    inputText: preset.inputText,
    errorColor: preset.errorColor,
    overlayBg: preset.overlayBg,
    unselectedBg: preset.unselectedBg,
    inactiveCircle: preset.inactiveCircle,
  };
};
